import { patchState, signalStore, withMethods, withProps, withState, withComputed } from '@ngrx/signals';
import { AbnahmeDetail, AbnahmeFormModel, AbnahmeMangel, AbnahmeSignatures } from './abnahme-protokoll.types';
import { Table, TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { inject } from '@angular/core';
import { APIService } from 'src/app/services/APIService/api.service';
import { filter, firstValueFrom, mergeMap } from 'rxjs';
import { Events, withEventHandlers } from '@ngrx/signals/events';
import { createdMangel } from '../mangelerfassungsdialog/mangeldialog.component';
import { Row } from 'src/app/models/ui/table';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { PreviewThumbnail } from '../../_shared/preview-thumbnails/preview-thumbnail.type';
import { FormGroupOfType } from 'src/app/types/shared';
import { FormControl, Validators } from '@angular/forms';

interface AbnahmeSessionId {
  auftragId: number;
  abnahmeId: number | null;
}
type BeginSessionResult = { resumed: true; previews: PreviewThumbnail[] } | { resumed: false };

interface AbnahmeProtokollState {
  sessionId: AbnahmeSessionId | null;
  auftragId: number | null;
  firmenId: number | null;
  abnahmeId: number | null;
  selectedObjekt: Row | null;
  objekts: Row[];
  maengel: Table<AbnahmeMangel>;
  maengelChanged: boolean;
  signaturesChanged: boolean;
  legacyValuesChanged: boolean;
  previewThumbnails: PreviewThumbnail[];
  deletedFiles: number[];
  signatures: AbnahmeSignatures;
}

const initialState: AbnahmeProtokollState = {
  sessionId: null,
  auftragId: null,
  firmenId: null,
  abnahmeId: null,
  selectedObjekt: null,
  objekts: [],
  maengel: TABLE_EMPTY(),
  maengelChanged: false,
  signaturesChanged: false,
  legacyValuesChanged: false,
  previewThumbnails: [],
  deletedFiles: [],
  signatures: { Pruefer: '', Kunde: '' },
};

export const ABNAHME_FORM_INITIAL: AbnahmeFormModel = {
  Pruefer: '',
  Datum: BackendLocaleDatePipe.now,
  Bemerkung: null,
  Freigabe: null,
  IsNeuMangel: false,
  IsErteilt: null,

  CHK1: false,
  CHK2: false,
  CHK3: false,
  CHK4: false,
  CHK5: false,
  CHK6: false,
  CHK7: false,
  CHK8: false,

  FUNC1: false,
  FUNC2: false,
  FUNC3: false,

  UnterschriftPrueferName: '',
  UnterschriftKundeName: '',
};
export const AbnahmeProtokollStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    events: inject(Events),
    api: inject(APIService),
    authService: inject(AuthService),
  })),
  withProps(() => ({
    form: new FormGroupOfType<AbnahmeFormModel>({
      Pruefer: new FormControl('', { nonNullable: true }),

      Datum: new FormControl(BackendLocaleDatePipe.now, {
        validators: Validators.required,
        nonNullable: true,
      }),

      Bemerkung: new FormControl(null),
      Freigabe: new FormControl(null, Validators.required),

      IsNeuMangel: new FormControl(false, { nonNullable: true }),
      IsErteilt: new FormControl(null, Validators.required),

      CHK1: new FormControl(false, { nonNullable: true }),
      CHK2: new FormControl(false, { nonNullable: true }),
      CHK3: new FormControl(false, { nonNullable: true }),
      CHK4: new FormControl(false, { nonNullable: true }),
      CHK5: new FormControl(false, { nonNullable: true }),
      CHK6: new FormControl(false, { nonNullable: true }),
      CHK7: new FormControl(false, { nonNullable: true }),
      CHK8: new FormControl(false, { nonNullable: true }),

      FUNC1: new FormControl(false, { nonNullable: true }),
      FUNC2: new FormControl(false, { nonNullable: true }),
      FUNC3: new FormControl(false, { nonNullable: true }),

      UnterschriftPrueferName: new FormControl('', {
        validators: Validators.required,
        nonNullable: true,
      }),

      UnterschriftKundeName: new FormControl('', {
        validators: Validators.required,
        nonNullable: true,
      }),
    }),
  })),
  withComputed(({ abnahmeId, selectedObjekt, maengel, signatures }) => ({
    isNew: () => abnahmeId() === null,
    isUnterschriebenPruefer: () => !!signatures.Pruefer(),
    isUnterschriebenKunde: () => !!signatures.Kunde(),
    mangelTable: () => {
      const ostammid = selectedObjekt()?.['ostammid'] as number;
      const table = maengel();
      if (!ostammid) return { ...table, rows: [] };

      const rows = table.rows.filter(row => row.OSTAMMID === ostammid);
      return { ...table, rows };
    },
  })),
  withMethods(store => ({
    beginSession(auftragId: number, firmenId: number, objekts: Row[], abnahmeId: number | null): BeginSessionResult {
      const incomingSessionId: AbnahmeSessionId = { auftragId, abnahmeId };
      if (isCurrentSession(store.sessionId(), incomingSessionId)) {
        return { resumed: true, previews: [...store.previewThumbnails()] };
      }

      store.form.reset({ ...ABNAHME_FORM_INITIAL });
      patchState(store, initialState);

      const Pruefer = store.authService.getToken().username;

      store.form.patchValue({
        Pruefer,
        UnterschriftPrueferName: Pruefer,
      });

      patchState(store, {
        sessionId: incomingSessionId,
        auftragId,
        firmenId,
        objekts,
        selectedObjekt: objekts[0],
        abnahmeId,
      });

      return { resumed: false };
    },

    loadAbnahme(abnahme: AbnahmeDetail) {
      const { ID, MangelIds, CHK9, CHK10, UnterschriftPruefer, UnterschriftKunde, ...formValue } = abnahme;

      const current = store.form.getRawValue();
      const missingPruefer = !formValue.Pruefer?.trim();
      const missingDatum = !formValue.Datum?.trim();

      store.form.reset({
        ...formValue,
        Pruefer: !missingPruefer ? formValue.Pruefer : current.Pruefer,
        Datum: !missingDatum ? formValue.Datum : current.Datum,
      });

      const signatures = {
        Pruefer: signatureImage(UnterschriftPruefer),
        Kunde: signatureImage(UnterschriftKunde),
      };

      patchState(store, { signatures, legacyValuesChanged: missingPruefer || missingDatum });
    },

    setMaengel(maengel: Table<AbnahmeMangel>) {
      patchState(store, { maengel });
    },

    setSelected(selectedObjekt: Row | null) {
      patchState(store, { selectedObjekt });
    },

    setDatum(Datum: string) {
      store.form.controls.Datum.setValue(Datum);
    },

    setPreviewThumbnails(previewThumbnails: PreviewThumbnail[]) {
      patchState(store, { previewThumbnails });
    },

    setDeletedFiles(deletedFiles: number[]) {
      patchState(store, { deletedFiles });
    },

    addDeletedFile(id: number) {
      patchState(store, {
        deletedFiles: [...store.deletedFiles(), id],
      });
    },

    setSignature(mode: 'Kunde' | 'Pruefer', signature: string | null) {
      const signatures = { ...store.signatures(), [mode]: signature };
      patchState(store, { signatures, signaturesChanged: true });
    },

    async addMangel(omazsid: number) {
      if (store.maengel().rows.some(row => row.OMAZSID === omazsid)) {
        return;
      }

      const row = await firstValueFrom(store.api.getAbnahmeMangel(omazsid));

      patchState(store, ({ maengel: { columns, rows } }) => {
        if (columns.length === 0) columns = Object.keys(row).map(key => ({ id: key }));

        return {
          maengel: { columns, rows: [...rows, row] },
          maengelChanged: true,
        };
      });
    },

    removeMangel(omazsid: number) {
      patchState(store, ({ maengel: { columns, rows } }) => {
        rows = rows.filter(row => row.OMAZSID !== omazsid);

        return {
          maengel: { columns, rows },
          maengelChanged: store.abnahmeId() === null ? rows.length > 0 : true,
        };
      });
    },

    updateAbnahme(abnahmeId: number | null) {
      abnahmeId ??= null;
      patchState(store, { abnahmeId });
    },

    markContentSaved() {
      store.form.markAsPristine();

      patchState(store, {
        signaturesChanged: false,
        maengelChanged: false,
        legacyValuesChanged: false,
      });
    },

    clear() {
      store.form.reset({ ...ABNAHME_FORM_INITIAL });
      patchState(store, initialState);
    },
  })),
  withEventHandlers(({ events, addMangel, auftragId }) => ({
    //* während Abnahme-Protokoll destroyed ist und MangelErfassung geöffnet ist
    addCreatedMangel$: events.on(createdMangel).pipe(
      filter(() => auftragId() !== null),
      mergeMap(async ({ payload: { omazsid } }) => {
        await addMangel(omazsid);
      })
    ),
  }))
);

function signatureImage(value: string): string {
  if (!value) return '';
  return value.startsWith('data:') ? value : `data:image/png;base64,${value}`;
}

function isCurrentSession(current: AbnahmeSessionId | null, incoming: AbnahmeSessionId): boolean {
  return current?.auftragId === incoming.auftragId && current?.abnahmeId === incoming.abnahmeId;
}
